import { Check, ChevronsUpDown, Search, X } from 'lucide-react';
import { type KeyboardEvent, useEffect, useId, useMemo, useRef, useState } from 'react';

import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

export interface TagMultiSelectProps {
  id?: string;
  options: string[];
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
}

const MAX_NAMES_SHOWN = 2;

/** A filter control: pick any number of the given tags from a searchable checklist. */
export function TagMultiSelect({
  id,
  options,
  value,
  onChange,
  placeholder = 'All tags',
  searchPlaceholder = 'Search tags…',
  emptyText = 'No matching tags',
  disabled = false,
}: TagMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const listId = useId();
  const searchRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();

    return query ? options.filter((option) => option.toLowerCase().includes(query)) : options;
  }, [options, search]);

  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  const summary =
    value.length === 0
      ? null
      : value.length <= MAX_NAMES_SHOWN
        ? value.join(', ')
        : `${value.length} tags selected`;

  function toggle(option: string): void {
    onChange(value.includes(option) ? value.filter((tag) => tag !== option) : [...value, option]);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => Math.min(index + 1, Math.max(filtered.length - 1, 0)));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const option = filtered[activeIndex];
      if (option !== undefined) toggle(option);
    }
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch('');
      }}
    >
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls={listId}
          disabled={disabled}
          className="flex h-9 w-full items-center justify-between gap-2 rounded-md border border-input bg-card px-3 text-sm shadow-xs outline-none transition-[color,box-shadow,border-color,background-color] duration-150 ease-out hover:border-ring/40 focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50"
        >
          {summary ? (
            <span className="truncate text-left font-medium">{summary}</span>
          ) : (
            <span className="truncate text-placeholder">{placeholder}</span>
          )}
          <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>

      <PopoverContent
        side="bottom"
        align="start"
        avoidCollisions
        className="flex max-h-72 w-(--radix-popover-trigger-width) min-w-56 max-w-[calc(100vw-2rem)] flex-col overflow-hidden p-0"
      >
        <div className="flex shrink-0 items-center gap-2 border-b border-border px-3 py-2">
          <Search className="size-4 shrink-0 text-muted-foreground" />
          <input
            ref={searchRef}
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setActiveIndex(0);
            }}
            onKeyDown={handleKeyDown}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            aria-controls={listId}
            aria-activedescendant={filtered[activeIndex] ? `${listId}-${activeIndex}` : undefined}
            className="w-full bg-transparent text-base outline-none placeholder:text-muted-foreground md:text-sm"
          />
        </div>

        <div
          id={listId}
          role="listbox"
          aria-multiselectable="true"
          aria-label={placeholder}
          className="min-h-0 flex-1 overflow-y-auto p-1"
        >
          {filtered.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground">{emptyText}</div>
          ) : (
            filtered.map((option, index) => {
              const selected = value.includes(option);

              return (
                <div
                  key={option}
                  id={`${listId}-${index}`}
                  role="option"
                  tabIndex={-1}
                  aria-selected={selected}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => toggle(option)}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return;
                    event.preventDefault();
                    toggle(option);
                  }}
                  className={cn(
                    'flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm select-none',
                    index === activeIndex && 'bg-accent text-accent-foreground',
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      'flex size-4 shrink-0 items-center justify-center rounded-sm border border-input bg-card',
                      selected && 'border-primary bg-primary text-primary-foreground',
                    )}
                  >
                    {selected ? <Check className="size-3" /> : null}
                  </span>
                  <span className="truncate">{option}</span>
                </div>
              );
            })
          )}
        </div>

        <div className="flex shrink-0 items-center justify-between border-t border-border px-2 py-1.5">
          <span className="px-1 text-xs text-muted-foreground">{value.length} selected</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={value.length === 0}
            onClick={() => onChange([])}
          >
            <X className="size-4" />
            Clear
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
